// Image caching utility for persistent image loading and display
// Uses the modern Cache API for persistence across refreshes and sessions

class ImageCache {
  private readonly CACHE_NAME = 'sighttrail-image-cache-v1';
  private readonly MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB
  private readonly CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

  /**
   * Get cached image or fetch and cache it
   */
  async getCachedImageUrl(originalUrl: string): Promise<string> {
    if (!originalUrl || originalUrl.startsWith('data:')) {
      return originalUrl;
    }

    // Bypass cache for known non-CORS or HTML page URLs to prevent ERR_FAILED console spam
    if (originalUrl.includes('unsplash.com/photos/')) {
      return originalUrl;
    }

    try {
      const cache = await caches.open(this.CACHE_NAME);
      const cachedResponse = await cache.match(originalUrl);

      if (cachedResponse) {
        const dateHeader = cachedResponse.headers.get('date');
        const cachedTime = dateHeader ? new Date(dateHeader).getTime() : Date.now();
        
        if (Date.now() - cachedTime < this.CACHE_DURATION) {
          // console.log('🚀 Using persistent cached image:', originalUrl);
          const blob = await cachedResponse.blob();
          return URL.createObjectURL(blob);
        } else {
          // Expired, delete and fetch fresh
          await cache.delete(originalUrl);
        }
      }

      // console.log('📥 Fetching and persisting image:', originalUrl);
      const response = await fetch(originalUrl, { mode: 'cors' });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      // Clone response before caching because it can only be read once
      const responseToCache = response.clone();
      await cache.put(originalUrl, responseToCache);
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('❌ Persistent cache failed:', error);
      return originalUrl; // Fallback
    }
  }

  /**
   * Preload and cache multiple images
   */
  async preloadImages(urls: string[]): Promise<void> {
    // console.log(`🔄 Preloading ${urls.length} images to persistent store...`);
    const promises = urls.map(url => this.getCachedImageUrl(url));
    await Promise.allSettled(promises);
    // console.log('✅ Persistent preloading completed');
  }

  /**
   * Clear all cached images
   */
  async clearCache(): Promise<void> {
    // console.log('🧹 Clearing persistent image cache...');
    await caches.delete(this.CACHE_NAME);
    // console.log('✅ Persistent cache cleared');
  }

  /**
   * Clear expired cache entries
   */
  async clearExpiredCache(): Promise<void> {
    const cache = await caches.open(this.CACHE_NAME);
    const keys = await cache.keys();
    const now = Date.now();

    for (const request of keys) {
      const response = await cache.match(request);
      if (response) {
        const dateHeader = response.headers.get('date');
        const cachedTime = dateHeader ? new Date(dateHeader).getTime() : now;
        if (now - cachedTime > this.CACHE_DURATION) {
          await cache.delete(request);
        }
      }
    }
  }

  /**
   * Format bytes to human readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Create singleton instance
export const imageCache = new ImageCache();

// Auto-cleanup expired cache every day
setInterval(() => {
  imageCache.clearExpiredCache();
}, 24 * 60 * 60 * 1000);

// Note: Removed window.addEventListener('beforeunload', ...) to ensure persistence across refreshes.
